import { useState, useRef } from "react"

import type { PromiseOrValue, Entries } from "@p-ban/common/src/types"
import { pick } from "@p-ban/common/src/utils/ObjectUtils"

type FormElements = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
type FocusableElements = FormElements | HTMLButtonElement | HTMLDivElement | HTMLElement

type Correctors<T> = Partial<{ [Key in keyof T]: (value: T[Key]) => T[Key] }>
type ValidatorResult = readonly string[] | undefined
type Validator<T> = (value: T) => PromiseOrValue<ValidatorResult>
type Validators<T> = Partial<{
  [Key in keyof T]: Validator<T[Key]>
}>
type Errors<T> = Partial<Record<keyof T, readonly string[]>>

export const compositeValidators = <T>(...validators: Validator<T>[]): Validator<T> => {
  const predicate = (results: ValidatorResult[]) => {
    const errorMessages = results.flat().filter((result): result is string => result !== undefined)
    if (errorMessages.length === 0) {
      return undefined
    }
    return errorMessages
  }

  return (value) => {
    const results = validators.map((validate) => validate(value))
    if (results.some((result) => result instanceof Promise)) {
      return Promise.all(results).then(predicate)
    }
    return predicate(results as ValidatorResult[])
  }
}

const useForm = <TValues extends Record<string, unknown>>(
  values: TValues,
  setValues: (update: (prev: TValues) => TValues) => void,
  {
    correctors = {},
    validators = {},
  }: {
    correctors?: Correctors<TValues>
    validators?: Validators<TValues>
  } = {},
) => {
  const [errors, setErrors] = useState<Errors<TValues>>({})
  const fieldRefs = useRef<Map<string, FocusableElements>>(new Map())

  const setFieldValue = <Name extends keyof TValues>(name: Name, value: TValues[Name]) => {
    setValues((prev) => ({ ...prev, [name]: value }))
  }

  const getFieldProps = <Name extends keyof TValues>(name: Name) => {
    const isTextLikeField = (e: React.ChangeEvent<FormElements> | React.FocusEvent<FormElements>) => {
      const textLikeTypes = ["text", "password", "email", "tel", "number", "url", "search"]
      return (
        e.target instanceof HTMLTextAreaElement ||
        (e.target instanceof HTMLInputElement && textLikeTypes.includes(e.target.type))
      )
    }

    const validate = (value: TValues[Name]) => {
      // レンダリング回数抑制のためawaitは使用せず、戻り値がPromise以外の場合は同期的に実行されるようにしています。
      const error = validators[name]?.(value)
      if (error instanceof Promise) {
        error.then((error) => setErrors((prev) => ({ ...prev, [name]: error })))
      } else {
        setErrors((prev) => ({ ...prev, [name]: error }))
      }
    }

    return {
      name,
      value: values[name],
      ref: (element: FormElements | null) => {
        if (element) {
          fieldRefs.current.set(name as string, element)
        } else {
          fieldRefs.current.delete(name as string)
        }
      },
      onChange(e: React.ChangeEvent<FormElements>) {
        const rawValue = e.target.value as TValues[Name]
        // テキスト入力の場合、correctorはonBlurで適用する（IME入力との相性のため）
        const value = isTextLikeField(e) ? rawValue : (correctors[name]?.(rawValue) ?? rawValue)
        setFieldValue(name, value)

        // ブラウザのオートコンプリート機能で入力されると、フォーカスされていない入力欄に対してもonChangeイベントが発生します。
        // そのためblurイベントが発生せず状態の更新や検証が行われなくなってしまうため、オートコンプリートされた入力は即時検証します。
        const isAutoCompleted = e.target !== document.activeElement

        // テキスト入力の場合changeイベントの度に検証するとパフォーマンスが悪くなるため、blurイベントで検証します。
        if (!isTextLikeField(e) || isAutoCompleted) {
          validate(value)
        }
      },
      onBlur: (e: React.FocusEvent<FormElements>) => {
        // テキスト入力の場合、correctorを適用してから検証を実行
        if (isTextLikeField(e)) {
          const rawValue = e.target.value as TValues[Name]
          const correctedValue = correctors[name]?.(rawValue) ?? rawValue
          setFieldValue(name, correctedValue)
          validate(correctedValue)
        }
      },
    }
  }

  const preSubmit = async () => {
    const errors: Errors<TValues> = {}
    const promises = (Object.entries(values) as Entries<TValues>).map(async ([name, value]) => {
      const error = await validators[name]?.(value)
      if (error !== undefined) {
        errors[name] = error
      }
    })

    await Promise.allSettled(promises)

    const errorFields = Object.keys(errors)
    const canSubmit = errorFields.length === 0
    if (canSubmit) {
      setErrors({})
    } else {
      setErrors(errors)
      const firstErrorField = errorFields[0]
      if (firstErrorField) {
        const element = fieldRefs.current.get(firstErrorField)
        element?.focus()
      }
    }

    return { canSubmit }
  }

  const getFieldErrors = <T extends keyof TValues>(...fields: [T, ...T[]]): readonly string[] | undefined => {
    const picked: Record<string, readonly string[] | undefined> = pick(errors, ...fields)
    const temp = (Object.values(picked).filter((value) => value !== undefined) as string[][]).flat()
    const messages = [...new Set(temp)]
    return messages.length === 0 ? undefined : messages
  }

  const getFieldRef = <Name extends keyof TValues>(name: Name) => {
    return (element: FocusableElements | null) => {
      if (element) {
        fieldRefs.current.set(name as string, element)
      } else {
        fieldRefs.current.delete(name as string)
      }
    }
  }

  return { setFieldValue, getFieldProps, getFieldErrors, getFieldRef, preSubmit }
}

export default useForm
